import type { WorkspaceLifecycleTransition } from './types';

export interface SwitchCandidate<TRuntime> {
	id: number;
	slug: string;
	runtime: TRuntime | null;
}

export type SwitchState<TRuntime> =
	| { phase: 'idle' }
	/** `outgoing` stays the active runtime until the candidate publishes. */
	| { phase: 'preparing'; candidate: SwitchCandidate<TRuntime>; outgoing: TRuntime | null }
	/** Inside the synchronous publish call. */
	| { phase: 'publishing'; candidateId: number; runtime: TRuntime; outgoing: TRuntime | null }
	| { phase: 'active'; runtime: TRuntime };

export type SwitchEvent<TRuntime, TPrepared> =
	| { type: 'switch'; slug: string; candidateId: number }
	| { type: 'runtimeCreated'; candidateId: number; runtime: TRuntime }
	| { type: 'prepared'; candidateId: number; prepared: TPrepared }
	| { type: 'candidateFailed'; candidateId: number; error: unknown }
	| { type: 'published' }
	| { type: 'publishFailed'; error: unknown }
	| { type: 'leave' };

export type SwitchEffect<TRuntime, TPrepared> =
	| { type: 'createRuntime'; candidateId: number; slug: string }
	| { type: 'prepare'; candidateId: number; runtime: TRuntime }
	| { type: 'publish'; prepared: TPrepared }
	| { type: 'unpublish' }
	| { type: 'dispose'; runtime: TRuntime }
	| { type: 'retire'; runtime: TRuntime }
	/** Always the last effect of a row so a re-entrant dispatch from a handler sees the fully applied row. */
	| { type: 'notify'; transition: WorkspaceLifecycleTransition };

export interface SwitchReduction<TRuntime, TPrepared> {
	state: SwitchState<TRuntime>;
	effects: SwitchEffect<TRuntime, TPrepared>[];
}

export class InvalidWorkspaceTransitionError extends Error {
	constructor(phase: SwitchState<unknown>['phase'], eventType: SwitchEvent<unknown, unknown>['type']) {
		super(`Workspace switch event "${eventType}" is not valid while "${phase}"`);
		this.name = 'InvalidWorkspaceTransitionError';
	}
}

interface Sluggable {
	slug: string;
}

/** Pure transition table for the workspace switch lifecycle; the manager runs the returned effects in order. */
export function reduceSwitch<TRuntime extends Sluggable, TPrepared>(
	state: SwitchState<TRuntime>,
	event: SwitchEvent<TRuntime, TPrepared>,
): SwitchReduction<TRuntime, TPrepared> {
	type State = SwitchState<TRuntime>;
	type Event = SwitchEvent<TRuntime, TPrepared>;
	type Effect = SwitchEffect<TRuntime, TPrepared>;
	type Reduction = SwitchReduction<TRuntime, TPrepared>;
	type Phase = State['phase'];
	type EventType = Event['type'];
	type Row<P extends Phase, E extends EventType> = (state: Extract<State, { phase: P }>, event: Extract<Event, { type: E }>) => Reduction;

	const invalid = () => new InvalidWorkspaceTransitionError(state.phase, event.type);
	const disposeIfPresent = (runtime: TRuntime | null): Effect[] => (runtime ? [{ type: 'dispose', runtime }] : []);
	const retireIfPresent = (runtime: TRuntime | null): Effect[] => (runtime ? [{ type: 'retire', runtime }] : []);
	const left = (runtime: TRuntime | null): Effect => ({ type: 'notify', transition: { type: 'left', slug: runtime?.slug ?? null } });
	const startCandidate = (slug: string, candidateId: number, outgoing: TRuntime | null, before: Effect[] = []): Reduction => ({
		state: { phase: 'preparing', candidate: { id: candidateId, slug, runtime: null }, outgoing },
		effects: [...before, { type: 'createRuntime', candidateId, slug }],
	});
	const abandonCandidate = ({ candidate, outgoing }: Extract<State, { phase: 'preparing' }>): Reduction => ({
		state: outgoing ? { phase: 'active', runtime: outgoing } : { phase: 'idle' },
		effects: disposeIfPresent(candidate.runtime),
	});

	const rows: { [P in Phase]?: { [E in EventType]?: Row<P, E> } } = {
		idle: {
			switch: (_, { slug, candidateId }) => startCandidate(slug, candidateId, null),
			leave: (state) => ({ state, effects: [] }),
		},
		active: {
			switch: (state, { slug, candidateId }) => {
				if (slug === state.runtime.slug) return { state, effects: [] };
				return startCandidate(slug, candidateId, state.runtime);
			},
			leave: ({ runtime }) => ({
				state: { phase: 'idle' },
				effects: [{ type: 'retire', runtime }, { type: 'unpublish' }, left(runtime)],
			}),
		},
		preparing: {
			switch: (state, { slug, candidateId }) => {
				if (slug === state.candidate.slug) return { state, effects: [] };
				if (state.outgoing && slug === state.outgoing.slug) return abandonCandidate(state);
				return startCandidate(slug, candidateId, state.outgoing, disposeIfPresent(state.candidate.runtime));
			},
			runtimeCreated: ({ candidate, outgoing }, { candidateId, runtime }) => {
				if (candidateId !== candidate.id || candidate.runtime) throw invalid();

				return {
					state: { phase: 'preparing', candidate: { ...candidate, runtime }, outgoing },
					effects: [{ type: 'prepare', candidateId: candidate.id, runtime }],
				};
			},
			prepared: ({ candidate, outgoing }, { candidateId, prepared }) => {
				if (candidateId !== candidate.id || !candidate.runtime) throw invalid();

				return {
					state: { phase: 'publishing', candidateId: candidate.id, runtime: candidate.runtime, outgoing },
					effects: [{ type: 'publish', prepared }],
				};
			},
			candidateFailed: (state, { candidateId }) => {
				if (candidateId !== state.candidate.id) throw invalid();
				return abandonCandidate(state);
			},
			leave: ({ candidate, outgoing }) => ({
				state: { phase: 'idle' },
				effects: [...disposeIfPresent(candidate.runtime), ...retireIfPresent(outgoing), { type: 'unpublish' }, left(outgoing)],
			}),
		},
		publishing: {
			published: ({ runtime, outgoing }) => ({
				state: { phase: 'active', runtime },
				effects: [
					...retireIfPresent(outgoing),
					{ type: 'notify', transition: { type: 'published', slug: runtime.slug, outgoing: outgoing?.slug ?? null } },
				],
			}),
			publishFailed: ({ runtime, outgoing }) => ({
				state: { phase: 'idle' },
				effects: [{ type: 'dispose', runtime }, ...disposeIfPresent(outgoing), left(outgoing)],
			}),
		},
	};

	// Indexing by two runtime discriminants yields a union of rows whose parameter types TS cannot re-correlate; the table's mapped type already guarantees each row matches its (phase, event) cell.
	const row = rows[state.phase]?.[event.type] as Row<Phase, EventType> | undefined;
	if (!row) throw invalid();
	return row(state, event);
}
